import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Guards the restore-and-save auto-save against firing more than once per page load.
// A single Turbo navigation can render twice (cached preview, then fresh network render),
// reconnecting this controller and calling save() again before the first POST clears
// localStorage — which would create a duplicate Monsterpiece. Keyed by storage key so the
// guard is shared across every controller instance in the page runtime.
const savesInFlight = new Set()

// Online coloring mode. Tap an SVG `.cell` region to fill it from the selected swatch; pan/
// pinch-zoom the stage; save (logged-in) a region->color map + a client-rendered PNG snapshot.
// No jQuery/GSAP — CSS `transition: fill` handles the fill animation.
export default class extends Controller {
  static targets = ["stage", "zoom", "swatch", "saveButton", "status", "toolButton"]
  static values = {
    saveUrl: String,
    newUrl: String,
    monsterpieceId: String,
    directUploadUrl: String,
    signedIn: Boolean,
    subscribed: Boolean,
    seedColors: Object,
    storageKey: String,
    restoreAndSave: Boolean,
    filename: String
  }

  connect() {
    this.svg = this.zoomTarget.querySelector("svg")
    this.regionColors = {}
    this.scale = 1
    this.tx = 0
    this.ty = 0
    this.pointers = new Map()
    this.tool = "fill"
    this.selectRandomStartColor()
    this._persist = this.debounce(() => this.persistLocal(), 400)

    this.applyState(this.seedFromValueOrStorage())

    if (this.restoreAndSaveValue && this.signedInValue && Object.keys(this.regionColors).length > 0) {
      this.save()
    }
  }

  // --- palette ---
  selectColor(event) {
    this.setActiveSwatch(event.currentTarget)
    this.setTool("fill") // picking a color returns to the paint bucket
  }

  // Start on a fun color, never white/gray/black.
  selectRandomStartColor() {
    const colorful = this.swatchTargets.filter((s) => !this.isNeutral(s.dataset.color))
    this.setActiveSwatch(colorful[Math.floor(Math.random() * colorful.length)] || this.swatchTargets[0])
  }

  isNeutral(hex) {
    return ["#FFFFFF", "#000000", "#7D7D7D", "#C0C0C0"].includes((hex || "").toUpperCase())
  }

  setActiveSwatch(swatch) {
    if (!swatch) return
    this.currentColor = swatch.dataset.color
    this.swatchTargets.forEach((s) => s.setAttribute("aria-pressed", s === swatch))
  }

  // --- tools ---
  selectTool(event) {
    this.setTool(event.currentTarget.dataset.tool)
  }

  setTool(tool) {
    this.tool = tool // "fill" | "erase"
    this.toolButtonTargets.forEach((b) => b.setAttribute("aria-pressed", b.dataset.tool === tool))
    // Swaps the cell cursor (bucket vs eraser) via a modifier class on the root.
    this.element.classList.toggle("coloring--erase", tool === "erase")
  }

  // --- fill / clear / random ---
  fillCell(cell) {
    const region = cell.dataset.region
    if (!region) return
    const color = this.tool === "erase" ? "#FFFFFF" : this.currentColor
    cell.setAttribute("fill", color)
    this.regionColors[region] = color
    this._persist()
  }

  clear() {
    if (!confirm("Clear all colors? This can't be undone.")) return
    this.cells().forEach((cell) => cell.setAttribute("fill", "#FFFFFF"))
    this.regionColors = {}
    this.persistLocal()
  }

  random() {
    if (Object.keys(this.regionColors).length > 0 && !confirm("Generate random colors? This overwrites what you've colored so far.")) return
    const palette = this.swatchTargets.map((s) => s.dataset.color).filter((c) => c.toUpperCase() !== "#FFFFFF")
    this.cells().forEach((cell) => {
      const color = palette[Math.floor(Math.random() * palette.length)]
      cell.setAttribute("fill", color)
      this.regionColors[cell.dataset.region] = color
    })
    this.persistLocal()
  }

  cells() {
    return this.svg ? Array.from(this.svg.querySelectorAll(".cell")) : []
  }

  applyState(map) {
    this.regionColors = { ...map }
    this.cells().forEach((cell) => {
      const color = this.regionColors[cell.dataset.region]
      if (color) cell.setAttribute("fill", color)
    })
  }

  seedFromValueOrStorage() {
    if (this.hasSeedColorsValue && Object.keys(this.seedColorsValue).length > 0) return this.seedColorsValue
    try {
      return JSON.parse(localStorage.getItem(this.storageKeyValue) || "{}")
    } catch {
      return {}
    }
  }

  persistLocal() {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(this.regionColors))
    } catch { /* quota / private mode — non-fatal */ }
  }

  // --- pan / pinch-zoom / click-to-fill ---
  // Fill on the standard click event (robust across input + synthetic clicks); the pointer
  // handlers below only pan/zoom and flag movement so a drag doesn't also paint.
  paint(event) {
    if (this._moved) {
      this._moved = false
      return
    }
    const cell = event.target.closest?.(".cell")
    if (cell) this.fillCell(cell)
  }

  pointerDown(event) {
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    this._moved = false
    this._lastDist = null
  }

  pointerMove(event) {
    if (!this.pointers.has(event.pointerId)) return
    const prev = this.pointers.get(event.pointerId)
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (this.pointers.size >= 2) {
      this.pinch()
      this._moved = true
      return
    }

    const dx = event.clientX - prev.x
    const dy = event.clientY - prev.y
    if (Math.abs(dx) + Math.abs(dy) > 4) this._moved = true
    if (this._moved) {
      this.tx += dx
      this.ty += dy
      this.applyTransform()
    }
  }

  pointerUp(event) {
    this.pointers.delete(event.pointerId)
  }

  pinch() {
    const pts = Array.from(this.pointers.values())
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
    if (this._lastDist) this.zoomBy(dist / this._lastDist)
    this._lastDist = dist
  }

  wheel(event) {
    event.preventDefault()
    this.zoomBy(event.deltaY < 0 ? 1.1 : 0.9)
  }

  zoomIn() { this.zoomBy(1.2) }
  zoomOut() { this.zoomBy(1 / 1.2) }
  zoomBy(factor) {
    this.scale = Math.min(8, Math.max(0.5, this.scale * factor))
    this.applyTransform()
  }
  resetView() {
    this.scale = 1
    this.tx = 0
    this.ty = 0
    this.applyTransform()
  }
  applyTransform() {
    this.zoomTarget.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
  }

  // --- snapshot + save ---
  get pageId() {
    return this.storageKeyValue.split(":")[1]
  }

  async snapshot() {
    const clone = this.svg.cloneNode(true)
    const vb = this.svg.viewBox?.baseVal
    const w = vb?.width || this.svg.clientWidth || 800
    const h = vb?.height || this.svg.clientHeight || 800
    clone.setAttribute("width", w)
    clone.setAttribute("height", h)

    const data = new XMLSerializer().serializeToString(clone)
    const url = URL.createObjectURL(new Blob([data], { type: "image/svg+xml;charset=utf-8" }))
    const img = new Image()
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = url
    })

    const maxDim = 1600
    const ratio = Math.min(1, maxDim / Math.max(w, h))
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(w * ratio)
    canvas.height = Math.round(h * ratio)
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = "#FFFFFF"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    URL.revokeObjectURL(url)

    return await new Promise((resolve) => canvas.toBlob(resolve, "image/png"))
  }

  async downloadPng() {
    const blob = await this.snapshot()
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `${this.filenameValue || "monsterpiece"}.png`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async save() {
    if (!this.signedInValue) {
      this.persistLocal()
      window.location = this.newUrlValue
      return
    }

    // Don't let a second connect()/click start another save for the same page while one is
    // already running — that's what produced duplicate Monsterpieces in the restore flow.
    if (savesInFlight.has(this.storageKeyValue)) return
    savesInFlight.add(this.storageKeyValue)

    this.setStatus("Saving…")
    this.saveButtonTarget && (this.saveButtonTarget.disabled = true)

    try {
      const blob = await this.snapshot()
      const file = new File([blob], "coloring.png", { type: "image/png" })
      const signedId = await this.directUpload(file)

      // Resuming your own monsterpiece updates it in place; otherwise create a new one.
      const resuming = this.hasMonsterpieceIdValue && this.monsterpieceIdValue
      const url = resuming ? `${this.saveUrlValue}/${this.monsterpieceIdValue}` : this.saveUrlValue

      const response = await fetch(url, {
        method: resuming ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          coloring_page_id: this.pageId,
          region_colors: this.regionColors,
          image: signedId
        })
      })

      if (!response.ok) throw new Error(`Save failed (${response.status})`)
      const json = await response.json()
      localStorage.removeItem(this.storageKeyValue)
      window.location = json.url
    } catch (error) {
      // Release the guard so the user can retry; on success we navigate away instead.
      savesInFlight.delete(this.storageKeyValue)
      console.error("[coloring] save failed", error)
      this.setStatus("Couldn't save — try again.")
      this.saveButtonTarget && (this.saveButtonTarget.disabled = false)
    }
  }

  directUpload(file) {
    return new Promise((resolve, reject) => {
      new DirectUpload(file, this.directUploadUrlValue).create((error, blob) => {
        error ? reject(error) : resolve(blob.signed_id)
      })
    })
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  debounce(fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn(...args), wait)
    }
  }
}
